/**
 * Helper function to get all accordion elements with their unique keys
 * Encapsulates the regex logic used throughout the code
 *
 * @returns {Array<{element: Element, uniqueKey: string}>} Array of accordion elements with their keys
 */
export const getAllAccordionElements = () => {
    const elements = [];
    document.querySelectorAll('[class*="uniqueKey:"]').forEach((el) => {
        const match = el.classList.value.match(/uniqueKey:\s*([^:]+(?:::[^:]+)*):/) || null;
        if (match) {
            elements.push({
                element: el,
                uniqueKey: match[1]
            });
        }
    });
    return elements;
};

/**
 * Helper function to extract unique key from an element's class list
 *
 * @param {Element} element - The DOM element to extract key from
 * @returns {string|null} The unique key or null if not found
 */
export const extractUniqueKey = (element) => {
    const match = element.classList.value.match(/uniqueKey:\s*([^:]+(?:::[^:]+)*):/) || null;
    return match ? match[1] : null;
};

/**
 * Helper function to determine if an accordion element is currently expanded
 * Uses the same logic as the working persistAccordionState function
 *
 * @param {Element} element - The accordion title element
 * @returns {boolean} True if the accordion is expanded
 */
export const isAccordionExpanded = (element) => {
    const isCurrentlyActive = element.classList.contains('active');
    const nextSibling = element.nextSibling || element.nextElementSibling;
    const contentIsActive = nextSibling && nextSibling.classList.contains('active');
    return isCurrentlyActive && contentIsActive;
};

/**
 * Helper function to expand a single accordion element
 * Encapsulates the DOM manipulation logic for expansion
 *
 * @param {Element} element - The accordion title element to expand
 */
export const expandAccordionElement = (element) => {
    const nextSibling = element.nextSibling || element.nextElementSibling;

    element.classList.remove('collapsable');
    element.classList.add('active');

    if (nextSibling) {
        nextSibling.classList.remove('collapsable');
        nextSibling.classList.add('active');
    }
};

/**
 * Helper function to collapse a single accordion element
 * Encapsulates the DOM manipulation logic for collapse
 *
 * @param {Element} element - The accordion title element to collapse
 */
export const collapseAccordionElement = (element) => {
    const nextSibling = element.nextSibling || element.nextElementSibling;

    element.classList.add('collapsable');
    element.classList.remove('active');

    if (nextSibling) {
        nextSibling.classList.add('collapsable');
        nextSibling.classList.remove('active');
    }
};

/**
 * Helper function to get the proper target element from a click event
 * Handles both title elements and their children
 *
 * @param {Event} event - The click event
 * @returns {Element} The target accordion title element
 */
export const getAccordionTarget = (event) => {
    return event.currentTarget.classList.contains('title') ? event.currentTarget : event.currentTarget.parentElement;
};

/**
 * Helper function to handle semantic-ui accordion structure
 * Specifically for .ui.accordion .title elements
 *
 * @param {Element} titleElement - The title element
 * @param {boolean} shouldExpand - Whether to expand or collapse
 */
export const handleSemanticUIAccordion = (titleElement, shouldExpand) => {
    const contentEl = titleElement.nextElementSibling;

    if (shouldExpand) {
        titleElement.classList.add('active');
        if (contentEl && contentEl.classList.contains('content')) {
            contentEl.classList.add('active');
        }
    } else {
        titleElement.classList.remove('active');
        titleElement.classList.add('collapsable');
        if (contentEl && contentEl.classList.contains('content')) {
            contentEl.classList.remove('active');
            contentEl.classList.add('collapsable');
        }
    }
};

/**
 * Helper function to check if all accordion elements are expanded
 * Used for determining tree state
 *
 * @param {string[]} expandedNodes - Array of expanded node keys
 * @returns {boolean} True if all accordions are expanded
 */
export const areAllAccordionsExpanded = (expandedNodes) => {
    const allElements = getAllAccordionElements();
    return allElements.every(({ uniqueKey }) => expandedNodes.includes(uniqueKey));
};
